import { forwardRef, useId, useState } from "react";
import type { ChangeEvent, InputHTMLAttributes } from "react";

import { cn } from "./utils";

export interface CampoNumericoProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "type"> {
  etiqueta: string;
  ayuda?: string;
  error?: string;
  /** Valor normalizado con punto decimal (ej. "12.5"), o "" si está vacío. */
  valor?: string;
  onCambiar?: (valorNormalizado: string) => void;
}

/** Acepta coma o punto como separador decimal y normaliza a punto. */
export function normalizarDecimal(texto: string): string {
  return texto.replace(/,/g, ".").replace(/[^0-9.]/g, "");
}

export const CampoNumerico = forwardRef<HTMLInputElement, CampoNumericoProps>(
  function CampoNumerico(
    { etiqueta, ayuda, error, valor, onCambiar, className, id, ...resto },
    ref,
  ) {
    const idGenerado = useId();
    const idCampo = id ?? idGenerado;
    const idAyuda = ayuda ? `${idCampo}-ayuda` : undefined;
    const idError = error ? `${idCampo}-error` : undefined;
    const describedBy = [idAyuda, idError].filter(Boolean).join(" ") || undefined;

    const [interno, setInterno] = useState(valor ?? "");
    const valorMostrado = valor ?? interno;

    function manejarCambio(evento: ChangeEvent<HTMLInputElement>) {
      const normalizado = normalizarDecimal(evento.target.value);
      setInterno(normalizado);
      onCambiar?.(normalizado);
    }

    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={idCampo} className="text-etiqueta text-texto">
          {etiqueta}
        </label>
        <input
          ref={ref}
          id={idCampo}
          inputMode="decimal"
          type="text"
          aria-describedby={describedBy}
          aria-invalid={Boolean(error) || undefined}
          value={valorMostrado.replace(".", ",")}
          onChange={manejarCambio}
          className={cn(
            "min-h-control rounded-control border border-borde bg-superficie px-3 text-cuerpo text-texto shadow-sm cifras",
            "focus-visible:border-primario focus-visible:shadow-foco",
            "disabled:cursor-not-allowed disabled:bg-fondo disabled:text-texto-secundario/50",
            error && "border-peligro",
            className,
          )}
          {...resto}
        />
        {ayuda ? (
          <p id={idAyuda} className="text-nota text-texto-secundario">
            {ayuda}
          </p>
        ) : null}
        {error ? (
          <p id={idError} className="text-nota text-peligro" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);
